import React from "react"
import type { FieldPath, FieldValues } from "react-hook-form"
import { useController } from "react-hook-form"

import { TextField } from "../../inputs/text-field/text-field"
import type { FormTextFieldProps } from "./form-text-field.types"

export const FormTextField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  id,
  error,
  helperText,
  preserveHelperTextSpacing = false,
  inputRef,
  ...rest
}: FormTextFieldProps<TFieldValues, TName>) => {
  const {
    field: { ref: fieldRef, ...restField },
    fieldState,
  } = useController({
    name,
  })

  const innerRef = React.useRef<HTMLInputElement | null>(null)

  React.useImperativeHandle(inputRef, () => innerRef.current)

  const helperTextValue = helperText ?? (preserveHelperTextSpacing ? " " : "")

  const inputSlot = rest.slotProps?.input
  // required is false to disable native browser ui
  const slotProps = {
    ...rest.slotProps,
    input:
      typeof inputSlot === "function"
        ? (ownerState: Parameters<typeof inputSlot>[0]) => ({
            ...inputSlot(ownerState),
            required: false,
          })
        : { ...inputSlot, required: false },
  }

  return (
    <TextField
      {...rest}
      {...restField}
      error={Boolean(fieldState.error) || error}
      helperText={fieldState.error ? fieldState.error.message : helperTextValue}
      id={id ?? name}
      inputRef={(node) => {
        fieldRef(node)
        innerRef.current = node as HTMLInputElement
      }}
      slotProps={slotProps}
    />
  )
}
